// src/app/auth/auth.service.ts
import { inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { jwtDecode } from 'jwt-decode';
import { Observable, throwError } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import * as CryptoJS from 'crypto-js';
import { AppConfig, buildApiUrl } from '../app.config';
import { Router } from '@angular/router';
import { UserInfo, UserInfoService } from '@app/shared/services/user-info.service';
import { ResetPassword } from '@app/modules/auth-layout/reset-password/reset-password.component';
import { ROUTES } from '@app/shared/constants/routes';
import { Register, RegisterResponse, VerifyEmail } from '@app/modules/auth-layout/register/register.component';
import { FullBusinessRegisterData, FullPersonRegisterData } from '@app/modules/auth-layout/register-form/register-form.component';
import { RegisterGoogle } from '@app/modules/auth-layout/google-callback/google-callback.component';
import { ProfileService } from '@app/modules/profile/profile.service';
import { ShipmentStoreService } from '@app/shared/services/shipment-store.service';
import { SelectionService } from '@app/shared/services/selection.service';
import { GoogleAuthService } from '@app/shared/services/google-auth.service';
import { FilterService } from '@app/shared/services/filter.service';
import { ShipmentsService } from '@app/modules/shipments/shipments.service';
import { Login, RefreshToken } from '@app/shared/components/utils';
import { BulkUploadService } from '@app/modules/BulkUpload/bulk-upload.service';
import { PaymentsService } from '@app/modules/payments/payments.service';
import { NotificationService } from '@app/modules/notifications/notification.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService implements OnDestroy {
  private http = inject(HttpClient);
  private router = inject(Router);
  private userInfoService = inject(UserInfoService);
  private profileService = inject(ProfileService);
  private shipmentStoreService = inject(ShipmentStoreService);
  private selection = inject(SelectionService);
  private googleAuth = inject(GoogleAuthService);
  private filterService = inject(FilterService);
  private shipmentService = inject(ShipmentsService);
  private bulkUploadService = inject(BulkUploadService);
  private paymentsService = inject(PaymentsService);
  private notificationService = inject(NotificationService);
  private isLoggedIn = false;
  private tokenCheckInterval: ReturnType<typeof setTimeout> | undefined;
  private emailJustVerified = false;
  public redirectUrl: string | null = null;
  private registrationEmail: string | null = null;
  private registrationIsSocialLogin = false;
  private pendingVerificationEmail: string | null = null;
  private pendingVerificationPassword: string | null = null;

  constructor() {
    this.startTokenCheck();
  }

  private startTokenCheck() {
    // Check token validity every minute
    this.tokenCheckInterval = setInterval(() => {
      if (this.isLoggedIn) {
        this.checkTokenValidity();
      }
    }, 60000);
  }

  private checkTokenValidity() {
    const userInfo = this.userInfoService.getUserInfo();
    if (!userInfo || this.isTokenExpired(userInfo)) {
      this.refreshToken().subscribe({
        error: () => this.logout().subscribe()
      });
    }
  }

  private isTokenExpired(userInfo: UserInfo): boolean {
    if (!userInfo.exp) return true;
    const expirationDate = new Date(userInfo.exp * 1000);
    return expirationDate <= new Date();
  }

  login(email: string, password: string, recaptchaToken: string): Observable<Login> {
    // Password hashing moved to backend
    const hashedPassword = CryptoJS.SHA512(password).toString();
    const body = { email, password: hashedPassword, recaptchaToken };

    return this.http.post<Login>(buildApiUrl(AppConfig.loginEndpoint), body, {
      withCredentials: true
    }).pipe(
      tap(response => {
        if (response.token) {
          const payload: UserInfo = jwtDecode(response.token);
          this.userInfoService.setUserInfo({...payload, token: response.token, reactivatedUser: response.reactivatedUser, statusCreditAccount: response.statusCreditAccount, hasOverdueInvoices: response.hasOverdueInvoices});
          this.isLoggedIn = true;
          void this.router.navigate([ROUTES.DASHBOARD]);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 429) {
          return throwError(() => new Error('Too many login attempts. Please try again later.'));
        }
        return throwError(() => error);
      })
    );
  }

  loginWithGoogle(token: string): Observable<Login>{
    const body = { token }
    return this.http.post<Login>(buildApiUrl(AppConfig.loginWithGoogle), body, {
      withCredentials: true
    }).pipe(
      tap(response => {
        if (response.token) {
          const payload: UserInfo = jwtDecode(response.token);
          this.userInfoService.setUserInfo({...payload, token: response.token, reactivatedUser: response.reactivatedUser, statusCreditAccount: response.statusCreditAccount, hasOverdueInvoices: response.hasOverdueInvoices});
          this.isLoggedIn = true;
          void this.router.navigate([ROUTES.DASHBOARD]);
        }
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  registerWithGoogle(body: RegisterGoogle, email:string): Observable<void>{
    return this.http.post<void>(buildApiUrl(AppConfig.registerWithGoogle), body, {
      withCredentials: false
    }).pipe(
      tap(() => {
        this.registrationIsSocialLogin = true;
        this.setRegistrationEmail(email);
        void this.router.navigate([ROUTES.REGISTER_FORM]);
      }),
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  refreshToken(): Observable<RefreshToken> {
    const url = buildApiUrl(AppConfig.refreshTokenEndpoint);

    return this.http.post<RefreshToken>(url, {}, {
      withCredentials: true
    }).pipe(
      tap(response => {
        if (response.token) {
          const payload: UserInfo = jwtDecode(response.token);
          this.userInfoService.setUserInfo({...payload, token: response.token, statusCreditAccount: response.statusCreditAccount, hasOverdueInvoices: response.hasOverdueInvoices});
          this.isLoggedIn = true;

          if (this.redirectUrl) {
            void this.router.navigate([this.redirectUrl]);
            this.redirectUrl = null;
          }
        }
      }),
      catchError(err => {
        this.isLoggedIn = false;
        this.userInfoService.setUserInfo(null);
        return throwError(() => err);
      })
    );
  }

  isAuthenticated(): boolean {
    const userInfo = this.userInfoService.getUserInfo();
    if (!userInfo) return false;
    return this.isLoggedIn && !this.isTokenExpired(userInfo);
  }

  logout(): Observable<void> {
    const url = buildApiUrl(AppConfig.logoutEndpoint);

    return this.http.post<void>(url, {}, {
      withCredentials: true
    }).pipe(
      tap(() => {
        this.cleanup();
      }),
      catchError((err) => {
        this.cleanup();
        return throwError(() => err);
      })
    );
  }

  private cleanup() {
    this.isLoggedIn = false;
    this.profileService.reset();
    this.shipmentStoreService.clear();
    this.shipmentService.clear();
    this.selection.clear();
    this.googleAuth.clear();
    this.filterService.clearAllFilters();
    this.userInfoService.setUserInfo(null);
    this.bulkUploadService.clearBulkImport();
    this.paymentsService.clear();
    this.notificationService.clear();
    clearInterval(this.tokenCheckInterval);
    void this.router.navigate([ROUTES.LOGIN]);
  }

  ngOnDestroy() {
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval);
    }
  }

  sendEmail(email: string): Observable<HttpResponse<void>> {
    const body = { email };
    return this.http.post<void>(buildApiUrl(AppConfig.forgotPassword), body, {
      withCredentials: false,
      observe: 'response'
    });
  }

  resetPassword(data: ResetPassword): Observable<HttpResponse<void>> {
    const hashedPassword = CryptoJS.SHA512(data.newPassword).toString();
    const hashedPasswordValid = CryptoJS.SHA512(data.confirmPassword).toString();

    const body: ResetPassword = {
      email: data.email,
      token: data.token,
      newPassword: hashedPassword,
      confirmPassword: hashedPasswordValid
    }
    return this.http.post<void>(buildApiUrl(AppConfig.resetPassword), body, {
      withCredentials: false,
      observe: 'response'
    });
  }

  registrationManual(data: Register): Observable<RegisterResponse> {
    const body = this.buildRegistrationBody(data);
    this.registrationIsSocialLogin = false;
    this.setRegistrationEmail(data.email);
    return this.http.post<RegisterResponse>(buildApiUrl(AppConfig.startRegister), body, {
      withCredentials: false
    });
  }

  startPendingRegistration(email: string, password: string): Observable<RegisterResponse> {
    const body = this.buildRegistrationBody({
      email,
      password,
      acceptedConditions: true,
      addNewsletters: false
    });
    this.registrationIsSocialLogin = false;
    this.setRegistrationEmail(email);
    return this.http.post<RegisterResponse>(buildApiUrl(AppConfig.startRegister), body, {
      withCredentials: false
    });
  }

  private buildRegistrationBody(data: Register): Register {
    const hashedPassword = CryptoJS.SHA512(data.password).toString();
    return {
      email: data.email,
      password: hashedPassword,
      acceptedConditions: data.acceptedConditions,
      addNewsletters: data.addNewsletters
    };
  }

  setRegistrationEmail(email: string): void {
    this.registrationEmail = email;
  }

  getRegistrationEmail(): string | null {
    return this.registrationEmail;
  }

  isRegistrationSocialLogin(): boolean {
    return this.registrationIsSocialLogin;
  }

  verifyRegisterMail(data: VerifyEmail): Observable<null> {
    return this.http.post<null>(buildApiUrl(AppConfig.verifyEmail), data, {
      withCredentials: false
    });
  }

  setPendingVerificationCredentials(email: string, password: string): void {
    this.pendingVerificationEmail = email;
    this.pendingVerificationPassword = password;
  }

  clearPendingVerificationCredentials(): void {
    this.pendingVerificationEmail = null;
    this.pendingVerificationPassword = null;
  }

  resendVerificationCode(): Observable<RegisterResponse> {
    if (!this.pendingVerificationEmail || !this.pendingVerificationPassword) {
      return throwError(() => new Error('No pending verification credentials'));
    }
    
    return this.startPendingRegistration(
      this.pendingVerificationEmail,
      this.pendingVerificationPassword
    );
  }

  setEmailJustVerified(value: boolean) {
    this.emailJustVerified = value;
  }

  canAccessPostRegisterRoute(): boolean {
    return this.emailJustVerified;
  }

  register(data: FullBusinessRegisterData | FullPersonRegisterData, legalPdf: File | null | undefined, taxPdf: File | null | undefined): Observable<HttpResponse<void>> {
    const formData = new FormData();
    for (const [key, value] of Object.entries(data)) {
      if (Array.isArray(value)) {
        for (const item of value) {
          formData.append(key, String(item));
        }
      } else if (value !== null && value !== undefined) {
        formData.append(key, String(value));
      }
    }
    if (legalPdf) {
      formData.append('legalPdfDocument', legalPdf);
    } 
    if (taxPdf) {
      formData.append('taxPdfDocument', taxPdf);
    } 


    return this.http.post<void>(buildApiUrl(AppConfig.register), formData, {
      withCredentials: false,
      observe: 'response'
    });
  }

  supportContact(){
    const phone = AppConfig.whatsappNumber;
    const url = "https://wa.me/"+ phone;
    window.open(url, '_blank');
  }
}
